import { useMemo, useState } from 'react';
import { ArrowLeftOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { Breadcrumb, Button, Card, Descriptions, Space, Spin, message, theme } from 'antd';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Permission } from '@aid/shared';
import { useHasPermission } from '../../auth/useHasPermission';
import { EmptyState } from '../../components/EmptyState';
import { PageHeader } from '../../components/PageHeader';
import { useCatalogos } from './api/useCatalogos';
import {
  useCategoriaAdmin,
  useCatalogoAdmin,
  useDeleteCatalogoAdmin,
} from './api/useCatalogosAdmin';
import { DeleteCatalogoConfirmModal } from './components/DeleteCatalogoConfirmModal';

export function CatalogoDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const catalogoId = Number(id);
  const { data: catalogo, isLoading, error } = useCatalogoAdmin(catalogoId);
  const { data: category } = useCategoriaAdmin(catalogo?.category ?? undefined);
  const parentCategoryCode = category?.parentCategoryCode ?? undefined;
  const { data: parentItems } = useCatalogos(parentCategoryCode ?? '', {
    limit: 1000,
    enabled: Boolean(parentCategoryCode),
  });
  const deleteCatalogo = useDeleteCatalogoAdmin();
  const canUpdate = useHasPermission(Permission.CATALOGOS_UPDATE);
  const canDelete = useHasPermission(Permission.CATALOGOS_DELETE);
  const { token } = theme.useToken();
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  const parentLabel = useMemo(() => {
    if (catalogo?.parent == null) return null;
    const parent = (parentItems ?? []).find((item) => Number(item.id) === Number(catalogo.parent));
    return parent?.value ?? parent?.key ?? `#${catalogo.parent}`;
  }, [catalogo?.parent, parentItems]);

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error || !catalogo) {
    return (
      <EmptyState
        title="Registro no encontrado"
        description="El registro que intentás consultar no existe o fue eliminado."
        action={
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/catalogos')}>
            Volver a catálogos
          </Button>
        }
      />
    );
  }

  const handleDelete = async () => {
    try {
      await deleteCatalogo.mutateAsync(Number(catalogo.id));
      message.success('Registro eliminado correctamente');
      navigate(
        category ? `/catalogos/category/${encodeURIComponent(category.code)}` : '/catalogos',
      );
    } catch {
      message.error('No se pudo eliminar el registro. Puede tener referencias o hijos.');
    }
  };

  return (
    <div>
      <PageHeader
        breadcrumb={
          <Breadcrumb
            items={[
              { title: <Link to="/">Inicio</Link> },
              {
                title: (
                  <Button
                    type="link"
                    onClick={() => navigate('/catalogos')}
                    style={{ padding: 0, height: 'auto' }}
                  >
                    Catálogos
                  </Button>
                ),
              },
              ...(category
                ? [
                    {
                      title: (
                        <Button
                          type="link"
                          onClick={() =>
                            navigate(`/catalogos/category/${encodeURIComponent(category.code)}`)
                          }
                          style={{ padding: 0, height: 'auto' }}
                        >
                          {category.name}
                        </Button>
                      ),
                    },
                  ]
                : []),
              { title: catalogo.value ?? `Registro #${catalogo.id}` },
            ]}
          />
        }
        title={catalogo.value ?? `Registro #${catalogo.id}`}
        subtitle={catalogo.key ?? undefined}
        actions={
          <Space wrap size={[8, 8]}>
            {canUpdate && (
              <Button
                icon={<EditOutlined />}
                onClick={() => navigate(`/catalogos/${catalogo.id}/edit`)}
              >
                Editar
              </Button>
            )}
            {canDelete && (
              <Button danger icon={<DeleteOutlined />} onClick={() => setDeleteModalOpen(true)}>
                Eliminar
              </Button>
            )}
          </Space>
        }
      />
      <Card
        title="Información del registro"
        style={{
          borderRadius: 12,
          border: `1px solid ${token.colorBorderSecondary}`,
          boxShadow: token.boxShadow,
        }}
        styles={{
          header: {
            borderBottom: `1px solid ${token.colorBorderSecondary}`,
            padding: '14px 22px',
          },
        }}
      >
        <Descriptions
          column={{ xs: 1, sm: 2 }}
          style={{ marginTop: 8 }}
          labelStyle={{
            color: token.colorTextSecondary,
            fontWeight: 500,
            paddingTop: 14,
            paddingBottom: 14,
          }}
          contentStyle={{ paddingTop: 14, paddingBottom: 14 }}
        >
          <Descriptions.Item label="ID">{catalogo.id}</Descriptions.Item>
          <Descriptions.Item label="Clave">{catalogo.key ?? '—'}</Descriptions.Item>
          <Descriptions.Item label="Valor">{catalogo.value ?? '—'}</Descriptions.Item>
          <Descriptions.Item label="Categoría">
            {category?.name ?? catalogo.category ?? '—'}
          </Descriptions.Item>
          {category?.behavior.hierarchy.enabled && (
            <Descriptions.Item label="Padre">{parentLabel ?? '—'}</Descriptions.Item>
          )}
          <Descriptions.Item label="Estado">
            {catalogo.active ? 'Activo' : 'Inactivo'}
          </Descriptions.Item>
        </Descriptions>
      </Card>
      <DeleteCatalogoConfirmModal
        open={deleteModalOpen}
        onConfirm={handleDelete}
        onCancel={() => setDeleteModalOpen(false)}
        loading={deleteCatalogo.isPending}
      />
    </div>
  );
}
